import { Component, Input, OnDestroy } from '@angular/core';
import { LotBuildProgrammeEventService } from '../../../services/lot-build-programme-event.service';
import {
    BUILD_PROGRAMME_ACTIVITY_STATUS_ENUM,
    IBuildProgrammeStageIndexedDto,
    ILotDto
} from '@classictechsolutions/hubapi-transpiled-enums';
import { LotBuildProgrammeActionBarService } from '../../services/lot-build-programme-action-bar.service';
import { IBuildProgrammeActivityMappedItem } from '@app/logic/build-programme-activity';
import { IBuildProgrammeActivitySummaryDto } from '@app/logic/build-programme/interfaces/i.build-programme-indexed.dto';
import { Subscription, take } from 'rxjs';
import { CdkDragDrop } from '@angular/cdk/drag-drop';

@Component({
    selector: 'cb-lot-build-programme-activity-table',
    templateUrl: './lot-build-programme-activity-table.component.html',
    styleUrls: ['./lot-build-programme-activity-table.component.scss'],
    standalone: false
})
export class LotBuildProgrammeActivityTableComponent implements OnDestroy {
    public static readonly RENDER_DELAY = 1000;
    @Input() public readonly lotDto: ILotDto;
    @Input() public readonly highlightedActivityId: number | null;

    public summaryExpanded = {} as { [buildActivityId: number]: boolean };
    public readonly BUILD_PROGRAMME_ACTIVITY_STATUS_ENUM = BUILD_PROGRAMME_ACTIVITY_STATUS_ENUM;
    public connectedDropListIds: string[] = [];

    public readonly displayedColumns: string[] = [
        'status',
        'code',
        'name',
        'supplier',
        'startDate',
        'endDate',
        'duration',
        'actions'
    ];
    public loaded = false;

    private _buildProgrammeStage: IBuildProgrammeStageIndexedDto;
    @Input() public set buildProgrammeStage(buildProgrammeStage: IBuildProgrammeStageIndexedDto) {
        this._buildProgrammeStage = buildProgrammeStage;
        if (buildProgrammeStage != null) {
            this.loaded = true;
            this.updateConnectedDropListIds();
        }
    }

    private updateConnectedDropListIds(): void {
        const allStages = this.lotBuildProgrammeEvents?.stages ?? [];
        const selfId = this.buildProgrammeStage?.buildStageId;
        this.connectedDropListIds = allStages
            .map(s => 'stage-drop-list-' + s.buildStageId)
            .filter(id => id !== 'stage-drop-list-' + selfId);
    }

    public onDrop(event: CdkDragDrop<unknown>): void {
        const fromStageId = this.parseStageIdFromDropListId((event.previousContainer?.id as string) || '');
        const toStageId = this.parseStageIdFromDropListId((event.container?.id as string) || '');
        if (!fromStageId || !toStageId) {
            return;
        }

        const fromList = this.lotBuildProgrammeEvents.activities[fromStageId] ?? [];
        const toList = this.lotBuildProgrammeEvents.activities[toStageId] ?? [];

        const realFromIdx = this.mapVisibleIndexToModelIndex(fromStageId, event.previousIndex);
        let realToIdx = this.mapVisibleIndexToModelIndex(toStageId, event.currentIndex);
        if (realFromIdx < 0 || realToIdx < 0) {
            return;
        }

        const [moved] = fromList.splice(realFromIdx, 1);
        if (!moved) {
            return;
        }

        if (fromStageId === toStageId && realFromIdx < realToIdx) {
            realToIdx -= 1;
        }

        if (fromStageId !== toStageId) {
            const stage = this.lotBuildProgrammeEvents.stages.find(s => s.buildStageId === toStageId);
            if (stage) {
                moved.buildStageId = stage.buildStageId;
                moved.buildProgrammeStageId = stage.id;
            } else {
                fromList.splice(realFromIdx, 0, moved);
                return;
            }
        }

        toList.splice(realToIdx, 0, moved);

        this.persistOrderAfterDrop(toStageId, moved.id, fromStageId !== toStageId ? fromStageId : undefined);

        this.updateConnectedDropListIds();
    }

    public get buildProgrammeStage():  IBuildProgrammeStageIndexedDto {
        return this._buildProgrammeStage;
    }

    private parseStageIdFromDropListId(listId: string): number {
        const n = Number((listId || '').replace('stage-drop-list-', ''));
        return Number.isFinite(n) ? n : 0;
    }

    private mapVisibleIndexToModelIndex(stageId: number, visibleIndex: number): number {
        const list = this.lotBuildProgrammeEvents.activities[stageId] ?? [];
        const include = (a: IBuildProgrammeActivityMappedItem) =>
            this.lotBuildProgrammeActionBar.viewManualOrders
                ? true
                : (!a.isFromManualPurchaseOrder && !a.manualOrderId);

        let seen = -1;
        for (let i = 0; i < list.length; i++) {
            if (include(list[i])) {
                seen++;
                if (seen === visibleIndex) { return i; }
            }
        }
        return visibleIndex >= 0 ? list.length : -1;
    }

    private persistOrderAfterDrop(targetStageId: number, movedActivityId?: number, oldStageId?: number): void {
        const target = this.lotBuildProgrammeEvents.activities[targetStageId] ?? [];
        target.forEach((activity, idx) => {
            const prev = activity.sortOrder;
            activity.sortOrder = idx + 1;
            if (prev !== activity.sortOrder || (movedActivityId && activity.id === movedActivityId)) {
                activity.saveOrder().pipe(take(1)).subscribe(res => this.lotBuildProgrammeEvents.ACTIVITY_RECEIVED.next([res]));
            }
        });

        if (oldStageId && oldStageId !== targetStageId) {
            const oldList = this.lotBuildProgrammeEvents.activities[oldStageId] ?? [];
            oldList.forEach((a, idx) => {
                const prev = a.sortOrder;
                a.sortOrder = idx + 1;
                if (prev !== a.sortOrder) {
                    a.saveOrder().pipe(take(1)).subscribe();
                }
            });
        }
    }

    private _renderTimeout: NodeJS.Timeout;
    private _render = false;
    /** for performance - render uses *ngIf to render the table when it should be visible */
    @Input() public set render(v: boolean) {
        clearTimeout(this._renderTimeout);
        if (!v) {
            this._renderTimeout = setTimeout(() => {
                this._render = v;
            }, LotBuildProgrammeActivityTableComponent.RENDER_DELAY);
        } else {
            this._render = v;
        }
    }

    public get render(): boolean {
        return this._render;
    }

    public get activities(): IBuildProgrammeActivityMappedItem[] {
        return this.filterManualOrders(this.lotBuildProgrammeEvents?.activities[this.buildProgrammeStage?.buildStageId]);
    }

    public get summaries(): IBuildProgrammeActivitySummaryDto[] {
        return (this.lotBuildProgrammeEvents?.summaries[this.buildProgrammeStage?.buildStageId] ?? []);
    }

    public get summaryActivties(): { [buildActivityId: number]: IBuildProgrammeActivityMappedItem[] } {
        return this.lotBuildProgrammeEvents?.summaryActivties[this.buildProgrammeStage?.buildStageId];
    }

    private sub$ = new Subscription();

    constructor(
        public readonly lotBuildProgrammeEvents: LotBuildProgrammeEventService,
        public readonly lotBuildProgrammeActionBar: LotBuildProgrammeActionBarService
    ) {
        this.sub$.add(
            this.lotBuildProgrammeActionBar.POST_EXPAND_COLLAPSE_ALL.subscribe(this.clearExpandedSummaryCache)
        );
    }

    public ngOnDestroy(): void {
        this.sub$.unsubscribe();
    }

    public getBlockActivities(): IBuildProgrammeActivityMappedItem[] {
        return this.filterManualOrders(this.activities)
            .filter(x => x.lotId === this.lotDto?.id);
    }

    public getSummaryActivties(buildActivityId): IBuildProgrammeActivityMappedItem[] {
        return this.filterManualOrders(this.summaryActivties[buildActivityId])
            .filter(x => x.lotId !== this.lotDto?.id);
    }

    private filterManualOrders(activities: IBuildProgrammeActivityMappedItem[]): IBuildProgrammeActivityMappedItem[] {
        return this.lotBuildProgrammeActionBar.viewManualOrders ? activities : activities.filter(x => !x.isFromManualPurchaseOrder && !x.manualOrderId);
    }

    public readonly activityRowClasses = (activity: IBuildProgrammeActivityMappedItem, highlightedActivityId: number): {
        [key: string]: boolean
    } => (
        {
            'table-pink': activity.isFromManualPurchaseOrder && activity.statusId !== BUILD_PROGRAMME_ACTIVITY_STATUS_ENUM.Abandoned,
            'table-olive': activity.isOwnersCare && activity.statusId !== BUILD_PROGRAMME_ACTIVITY_STATUS_ENUM.Abandoned,
            'table-grey': activity.isControlledByParentLot && activity.statusId !== BUILD_PROGRAMME_ACTIVITY_STATUS_ENUM.Abandoned,
            'table-abandoned': activity.statusId === BUILD_PROGRAMME_ACTIVITY_STATUS_ENUM.Abandoned,
            // eslint-disable-next-line quote-props
            'highlight': activity.id === highlightedActivityId
        }
    );
    private readonly clearExpandedSummaryCache = (allExpanded: boolean): void => {
        if (!allExpanded) {
            this.summaryExpanded = {};
        }
    };
}
